/**
 * DraggableKeyframe Component
 *
 * P0.4: Keyframe card that can be:
 * - Dragged to Start/End Image slots
 * - Clicked to "Use as Start Image"
 * - Edited with Nano Editor
 *
 * This component ensures that displayed keyframes correspond to real persisted assets.
 */

import * as React from 'react';
import { useMemo, useCallback, useState } from 'react';
import { ImageFile, StoryboardPreview } from '../types';
import { ImageAsset } from '../types/project';
import { SparklesIcon, PhotoIcon, ArrowRightIcon } from './icons';

interface DraggableKeyframeProps {
    /** Segment index (0 = root, 1+ = extensions) */
    segmentIndex: number;
    /** The storyboard preview data */
    preview: StoryboardPreview;
    /** Associated ImageAsset if already persisted */
    asset?: ImageAsset | null;
    /** Callback when user wants to edit with Nano */
    onEdit?: (segmentIndex: number, image: ImageFile, prompt: string) => void;
    /** Callback when user clicks "Use as Start Image" */
    onUseAsStartImage?: (segmentIndex: number, image: ImageFile) => void;
    /** Callback when user clicks "Use as End Image" */
    onUseAsEndImage?: (segmentIndex: number, image: ImageFile) => void;
    /** Whether this keyframe is currently being used as start image */
    isActiveStartImage?: boolean;
    /** Whether generation is in progress for this segment */
    isGenerating?: boolean;
    /** Label to display */
    label?: string;
    /** Border color class */
    borderColor?: string;
    /** Prompt for this segment */
    prompt?: string;
    /** Size variant */
    size?: 'small' | 'medium' | 'large';
}

// MIME type for custom drag data
const DRAG_MIME_TYPE = 'application/x-jenial-image-asset';

export const DraggableKeyframe: React.FC<DraggableKeyframeProps> = ({
    segmentIndex,
    preview,
    asset,
    onEdit,
    onUseAsStartImage,
    onUseAsEndImage,
    isActiveStartImage = false,
    isGenerating = false,
    label,
    borderColor = 'border-indigo-500/50',
    prompt = '',
    size = 'medium',
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [showActions, setShowActions] = useState(false);

    // Compute image URL
    const imageUrl = useMemo(() => {
        if (!preview?.previewImage) return null;

        const img = preview.previewImage;
        // Prefer base64 (more reliable)
        if (img.base64) {
            if (img.base64.startsWith('data:')) {
                return img.base64;
            }
            return `data:image/png;base64,${img.base64}`;
        }

        // Fallback to file URL if available
        if (img.file) {
            try {
                return URL.createObjectURL(img.file);
            } catch {
                return null;
            }
        }

        return null;
    }, [preview?.previewImage]);

    // Handle drag start
    const handleDragStart = useCallback((e: React.DragEvent) => {
        if (!preview?.previewImage) return;

        setIsDragging(true);

        // Set drag data
        const dragData = JSON.stringify({
            segmentIndex,
            assetId: asset?.id,
            type: 'keyframe',
            imageBase64: preview.previewImage.base64,
        });

        e.dataTransfer.setData(DRAG_MIME_TYPE, dragData);
        e.dataTransfer.setData('text/plain', imageUrl || '');
        e.dataTransfer.effectAllowed = 'copy';

        // Set drag image
        if (e.currentTarget instanceof HTMLElement) {
            const rect = e.currentTarget.getBoundingClientRect();
            e.dataTransfer.setDragImage(e.currentTarget, rect.width / 2, rect.height / 2);
        }

        console.log('[DraggableKeyframe] Drag started:', { segmentIndex, assetId: asset?.id });
    }, [segmentIndex, asset?.id, preview?.previewImage, imageUrl]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Handle "Use as Start Image" click
    const handleUseAsStart = useCallback(() => {
        if (preview?.previewImage && onUseAsStartImage) {
            onUseAsStartImage(segmentIndex, preview.previewImage);
        }
    }, [segmentIndex, preview?.previewImage, onUseAsStartImage]);

    // Handle "Use as End Image" click
    const handleUseAsEnd = useCallback(() => {
        if (preview?.previewImage && onUseAsEndImage) {
            onUseAsEndImage(segmentIndex, preview.previewImage);
        }
    }, [segmentIndex, preview?.previewImage, onUseAsEndImage]);

    // Handle edit click
    const handleEdit = useCallback(() => {
        if (preview?.previewImage && onEdit) {
            onEdit(segmentIndex, preview.previewImage, prompt);
        }
    }, [segmentIndex, preview?.previewImage, prompt, onEdit]);

    // Size classes
    const sizeClasses = {
        small: 'w-20',
        medium: 'w-32',
        large: 'w-48',
    };

    if (!preview?.previewImage || !imageUrl) {
        return (
            <div className={`${sizeClasses[size]} aspect-video bg-gray-800 rounded-lg border border-dashed border-gray-600 flex items-center justify-center`}>
                <PhotoIcon className="w-6 h-6 text-gray-600" />
            </div>
        );
    }

    return (
        <div
            className={`${sizeClasses[size]} relative group`}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {/* Main Image Container */}
            <div
                draggable
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                className={`
                    aspect-video rounded-lg overflow-hidden border-2 relative cursor-grab
                    ${borderColor}
                    ${isDragging ? 'opacity-50 scale-95' : ''}
                    ${isActiveStartImage ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-gray-900' : ''}
                    transition-all duration-150
                `}
            >
                <img
                    src={imageUrl}
                    className="w-full h-full object-cover"
                    alt={label || `Keyframe ${segmentIndex}`}
                    draggable={false}
                />

                {/* Generation Overlay */}
                {isGenerating && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                    </div>
                )}

                {/* Active Start Image Badge */}
                {isActiveStartImage && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-green-500 text-white text-[8px] font-bold rounded shadow">
                        START
                    </div>
                )}

                {/* Label Badge */}
                {label && !isActiveStartImage && (
                    <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[8px] font-medium rounded">
                        {label}
                    </div>
                )}

                {/* Persisted Badge */}
                {asset && (
                    <div className="absolute top-1 right-1 w-2 h-2 bg-green-400 rounded-full" title="Persisted Asset" />
                )}

                {/* Hover Actions Overlay */}
                {showActions && !isGenerating && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-1.5 animate-in fade-in duration-100">
                        {/* Use as Start Image */}
                        {onUseAsStartImage && !isActiveStartImage && (
                            <button
                                onClick={handleUseAsStart}
                                className="px-2 py-1 bg-green-500 hover:bg-green-400 text-white text-[9px] font-semibold rounded shadow flex items-center gap-1 transition-colors"
                            >
                                <ArrowRightIcon className="w-3 h-3" />
                                Start Image
                            </button>
                        )}

                        {/* Edit with Nano */}
                        {onEdit && (
                            <button
                                onClick={handleEdit}
                                className="px-2 py-1 bg-orange-500 hover:bg-orange-400 text-white text-[9px] font-semibold rounded shadow flex items-center gap-1 transition-colors"
                            >
                                <SparklesIcon className="w-3 h-3" />
                                Retoucher
                            </button>
                        )}

                        {/* Use as End Image (for extensions) */}
                        {onUseAsEndImage && segmentIndex > 0 && (
                            <button
                                onClick={handleUseAsEnd}
                                className="px-2 py-1 bg-purple-500 hover:bg-purple-400 text-white text-[9px] font-semibold rounded shadow flex items-center gap-1 transition-colors"
                            >
                                <ArrowRightIcon className="w-3 h-3" />
                                End Image
                            </button>
                        )}
                    </div>
                )}

                {/* Drag Hint */}
                <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[8px] text-white/60 bg-black/40 px-1 rounded">Drag</span>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// DROP TARGET COMPONENT
// ============================================================================

interface KeyframeDropTargetProps {
    /** Target type */
    targetType: 'start' | 'end';
    /** Current image in the slot */
    currentImage?: ImageFile | null;
    /** Callback when image is dropped */
    onDrop: (image: ImageFile, assetId?: string) => void;
    /** Callback when image is cleared */
    onClear?: () => void;
    /** Label */
    label?: string;
    /** Whether drop is currently allowed */
    disabled?: boolean;
}

export const KeyframeDropTarget: React.FC<KeyframeDropTargetProps> = ({
    targetType,
    currentImage,
    onDrop,
    onClear,
    label,
    disabled = false,
}) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        if (disabled) return;

        // Check if we have valid drag data
        if (e.dataTransfer.types.includes(DRAG_MIME_TYPE)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            setIsDragOver(true);
        }
    }, [disabled]);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        if (disabled) return;

        const dragDataStr = e.dataTransfer.getData(DRAG_MIME_TYPE);
        if (!dragDataStr) return;

        try {
            const dragData = JSON.parse(dragDataStr);
            console.log('[KeyframeDropTarget] Received drop:', dragData);

            if (dragData.imageBase64) {
                // Reconstruct ImageFile from drag data
                const imageFile: ImageFile = {
                    file: null as any,  // File object not transferable, will be reconstructed if needed
                    base64: dragData.imageBase64,
                };

                onDrop(imageFile, dragData.assetId);
            }
        } catch (error) {
            console.error('[KeyframeDropTarget] Failed to parse drag data:', error);
        }
    }, [disabled, onDrop]);

    const imageUrl = useMemo(() => {
        if (!currentImage) return null;
        if (currentImage.base64) {
            if (currentImage.base64.startsWith('data:')) {
                return currentImage.base64;
            }
            return `data:image/png;base64,${currentImage.base64}`;
        }
        if (currentImage.file) {
            try {
                return URL.createObjectURL(currentImage.file);
            } catch {
                return null;
            }
        }
        return null;
    }, [currentImage]);

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
                relative aspect-video rounded-lg border-2 border-dashed overflow-hidden transition-all
                ${isDragOver ? 'border-green-400 bg-green-900/30 scale-105' : 'border-gray-600 bg-gray-800/50'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
        >
            {currentImage && imageUrl ? (
                <div className="relative w-full h-full">
                    <img
                        src={imageUrl}
                        className="w-full h-full object-cover"
                        alt={`${targetType} image`}
                    />
                    {onClear && (
                        <button
                            onClick={onClear}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center text-white text-xs"
                        >
                            ×
                        </button>
                    )}
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[9px] font-medium rounded">
                        {targetType === 'start' ? 'Start Image' : 'End Image'}
                    </div>
                </div>
            ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                    <PhotoIcon className="w-8 h-8 mb-1 opacity-30" />
                    <span className="text-xs">{label || `Drop ${targetType === 'start' ? 'Start' : 'End'} Image`}</span>
                    {isDragOver && (
                        <span className="text-xs text-green-400 mt-1">Release to set</span>
                    )}
                </div>
            )}
        </div>
    );
};

export default DraggableKeyframe;
